import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';

// --- Configuración ---
const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://znexxus.mx';
const API_URL = `${BACKEND_URL}/api/whatsapp`;

// --- CONEXIÓN DE SOCKET.IO CON EL PATH CORRECTO ---
const socket = io(BACKEND_URL, {
  transports: ['websocket'],
  path: '/api/socket.io'
});

// --- BASE DE DATOS MOCK (Simulación de tu CRM) ---
// En una app real, estos datos vendrían de tu API al cargar la página
// o al recibir el primer mensaje de un número desconocido.
const mockCrmDatabase = {
  '5218112345678': {
    tipo: 'prospecto',
    empresa: 'Llantera del Norte S.A. de C.V.',
    rfc: 'LNO980101ABC',
    contacto: 'Carlos Rodríguez',
    puesto: 'Gerente de Compras',
    telefono: '+52 81 1234 5678',
    email: 'carlos.r@llanteranorte.com',
    origen: 'Expo',
    semaforo: 'amarillo', // 'verde', 'amarillo', 'rojo'
    etapa: 'Negociación', // 'Nuevo', 'Contactado', 'Negociacion', 'Alta', 'Perdido'
    adicionales: {
      categoria: 'Flotillas',
      facturacionMensual: '$150,000',
      credito: 'SÍ',
      plazoDias: 30,
    },
    entregas: {
      direccion: 'Av. Industria 123, Apodaca, N.L.',
      horario: 'L-V 9:00 - 17:00',
      encargado: 'Raúl (Almacén)',
    },
    notas: '- 28/Oct: Carlos solicita cotización para 500 unidades. Se ve interesado.\n- 15/Oct: Contacto inicial en Expo.'
  },
  '5213398765432': {
    tipo: 'cliente',
    empresa: 'Refaccionaria GDL',
    rfc: 'RGD850505XYZ',
    contacto: 'Luis Mendoza',
    puesto: 'Dueño',
    telefono: '+52 33 9876 5432',
    email: 'luis.m@refagdl.com',
    origen: 'Internet',
    semaforo: 'rojo',
    diasSinCompra: 92,
    adicionales: {
      categoria: 'Mostrador',
      facturacionMensual: '$80,000',
      credito: 'NO',
      plazoDias: 0,
    },
    entregas: {
      direccion: 'Calle Obregón 500, Guadalajara, JAL.',
      horario: 'L-S 10:00 - 18:00',
      encargado: 'Mismo',
    },
    notas: '- 01/Ago: Última compra. Vendedor debe contactar.'
  },
  '5215511223344': {
    tipo: 'prospecto',
    empresa: 'Transportes Fénix',
    rfc: 'TFE010203ABC',
    contacto: 'Ana Torres',
    puesto: 'Logística',
    telefono: '+52 55 1122 3344',
    email: 'ana.torres@fenix.com',
    origen: 'Referido',
    semaforo: 'verde',
    etapa: 'Nuevo',
    adicionales: {
      categoria: 'Transporte Pesado',
      facturacionMensual: 'N/D',
      credito: 'Pendiente',
      plazoDias: 0,
    },
    entregas: {
      direccion: 'Vallejo 100, CDMX',
      horario: 'Solo mañanas',
      encargado: 'Ana Torres',
    },
    notas: 'Nuevo prospecto referido por "Servicio Potosino". Contactar.'
  }
};
// --------------------------------------------------

// Helper para formatear la hora
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  // El timestamp de WhatsApp viene en segundos, JS usa milisegundos
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

// --- Componente Principal: Estación de Trabajo ---
function WhatsappWorkstation() {
  const [conversations, setConversations] = useState({});
  const [currentTarget, setCurrentTarget] = useState(null);
  const [inputText, setInputText] = useState('');
  const chatEndRef = useRef(null); // Para auto-scroll
  const [isConnected, setIsConnected] = useState(socket.connected); // Estado de conexión
  const [activeTab, setActiveTab] = useState('prospectos'); // 'prospectos' o 'clientes'

  useEffect(() => {
    // Función para manejar mensajes nuevos
    const handleNewMessage = (message) => {
      console.log('¡Mensaje nuevo recibido!:', message);
      const from = message.from;

      setConversations(prevConvos => {
        const updatedConvos = { ...prevConvos };
        if (!updatedConvos[from]) {
          updatedConvos[from] = { messages: [] };
        }
        updatedConvos[from].messages = [...updatedConvos[from].messages, message];
        return updatedConvos;
      });
      
      // Si es el primer mensaje general y no hay target, selecciona este chat
      if (!currentTarget && Object.keys(conversations).length === 0) {
        setCurrentTarget(from);
        // Lógica de pestañas: Si el nuevo chat es un prospecto, ve a esa pestaña
        if (mockCrmDatabase[from]?.tipo === 'prospecto') {
          setActiveTab('prospectos');
        } else {
          setActiveTab('clientes');
        }
      }
    };

    // Listeners de Socket.IO
    socket.on('connect', () => {
      console.log('Conectado al servidor de sockets:', socket.id);
      setIsConnected(true);
    });
    socket.on('disconnect', () => {
      console.log('Desconectado del servidor de sockets');
      setIsConnected(false);
    });
    socket.on('newMessage', handleNewMessage);
    socket.on('connect_error', (err) => {
      console.error('Error de conexión Socket.IO:', err.message);
      setIsConnected(false);
    });

    // Limpieza al desmontar
    return () => {
      console.log('Desmontando WhatsappWorkstation, quitando listeners...');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('newMessage', handleNewMessage);
      socket.off('connect_error');
    };
  }, [currentTarget, conversations]); // Dependencias

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations, currentTarget]);

  // Enviar mensaje
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !currentTarget) return;

    const agentMessage = {
      from: 'agent',
      text: inputText,
      timestamp: Math.floor(Date.now() / 1000),
    };

    try {
      // Optimista
      setConversations(prevConvos => {
        const updatedConvos = { ...prevConvos };
        if (!updatedConvos[currentTarget]) updatedConvos[currentTarget] = { messages: [] };
        updatedConvos[currentTarget].messages = [...updatedConvos[currentTarget].messages, agentMessage];
        return updatedConvos;
      });
      setInputText('');

      // Envío API
      await axios.post(`${API_URL}/send`, {
        to: currentTarget,
        text: inputText,
      });

    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      // alert('No se pudo enviar el mensaje.'); // Evitar alerts
      // Revertir
      setConversations(prevConvos => {
          const updatedConvos = { ...prevConvos };
          updatedConvos[currentTarget].messages = updatedConvos[currentTarget].messages.slice(0, -1);
          return updatedConvos;
      });
      setInputText(agentMessage.text);
    }
  };

  // --- Lógica de Renderizado ---
  
  // Datos del CRM para el target seleccionado
  const selectedCrmData = mockCrmDatabase[currentTarget];
  
  // Mensajes para el target seleccionado
  const currentMessages = conversations[currentTarget]?.messages || [];

  // Filtrar los IDs de conversación basados en el tipo de CRM y la pestaña activa
  const conversationIDs = Object.keys(conversations);
  const filteredIDs = conversationIDs.filter(id => {
    const crmInfo = mockCrmDatabase[id];
    const tipo = crmInfo ? crmInfo.tipo : 'prospecto'; // Asumir 'prospecto' si no se encuentra
    return tipo === activeTab;
  });

  return (
    <div className="flex h-[calc(100vh-100px)] bg-gray-100 rounded-lg shadow-xl border border-gray-200">

      {/* ------------------------------------------------------ */}
      {/* PANEL IZQUIERDO: Tablero de Vendedor (Clientes y Prospectos) */}
      {/* ------------------------------------------------------ */}
      <div className="w-full md:w-1/4 h-full flex flex-col bg-white border-r border-gray-200">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Tablero de Vendedor</h1>
          <span className="text-sm text-gray-500">Agente: Ana L.</span>
          <span className={`ml-4 h-2 w-2 rounded-full inline-block ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} title={isConnected ? 'Conectado' : 'Desconectado'}></span>
        </div>
        
        {/* Buscador */}
        <div className="p-4">
          <input type="text" placeholder="Buscar por RFC, Empresa o Nombre..." className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>

        {/* Pestañas de Clientes / Prospectos */}
        <div className="flex border-b border-gray-200">
          <button 
            onClick={() => setActiveTab('prospectos')}
            className={`flex-1 p-3 text-sm font-semibold ${activeTab === 'prospectos' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            Prospectos
          </button>
          <button 
            onClick={() => setActiveTab('clientes')}
            className={`flex-1 p-3 text-sm font-semibold ${activeTab === 'clientes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            Clientes
          </button>
        </div>

        {/* Lista de Clientes/Prospectos */}
        <div className="flex-1 overflow-y-auto thin-scrollbar">
          {filteredIDs.length === 0 && (
            <p className="p-4 text-sm text-gray-500 text-center">No hay {activeTab} con mensajes.</p>
          )}

          {filteredIDs.map(wa_id => {
            const crmInfo = mockCrmDatabase[wa_id] || { empresa: wa_id, semaforo: 'gris', etapa: 'Desconocido' };
            const convo = conversations[wa_id];
            const lastMessage = convo.messages.length > 0 ? convo.messages.slice(-1)[0] : null;

            const semaforoColor = {
              verde: 'bg-green-500',
              amarillo: 'bg-yellow-400',
              rojo: 'bg-red-500',
              gris: 'bg-gray-400'
            }[crmInfo.semaforo];

            return (
              <div 
                key={wa_id}
                onClick={() => setCurrentTarget(wa_id)}
                className={`client-item flex items-center p-4 border-b border-gray-200 cursor-pointer ${currentTarget === wa_id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              >
                <span className={`w-3 h-3 ${semaforoColor} rounded-full flex-shrink-0`} title={`Semaforo: ${crmInfo.semaforo}`}></span>
                <div className="flex-1 min-w-0 mx-3">
                  <p className="text-sm font-semibold text-gray-900 truncate">{crmInfo.empresa}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {lastMessage ? lastMessage.text : (crmInfo.etapa || `Días sin Compra: ${crmInfo.diasSinCompra || 0}`)}
                  </p>
                </div>
                {/* Aquí podrías agregar un contador de mensajes no leídos si tuvieras esa lógica */}
              </div>
            );
          })}
        </div>
      </div>

      {/* ------------------------------------------------------ */}
      {/* PANEL CENTRAL: Centro de Actividad (Chat y Seguimiento) */}
      {/* ------------------------------------------------------ */}
      <div className="w-full md:w-1/2 h-full flex flex-col bg-gray-50">
        {!currentTarget ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Selecciona un chat para empezar.</p>
          </div>
        ) : (
          <>
            {/* Header del Chat */}
            <div className="flex items-center p-4 border-b border-gray-200 bg-white shadow-sm">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">{selectedCrmData?.empresa || currentTarget}</h2>
                <p className="text-sm text-gray-500">
                  {selectedCrmData?.contacto || 'Contacto Desconocido'}
                  <span className="mx-2">|</span>
                  {currentTarget}
                </p>
              </div>
            </div>

            {/* Historial de Chat */}
            <div id="chat-history" className="flex-1 p-4 flex flex-col overflow-y-auto thin-scrollbar">
              {currentMessages.map((msg, index) => (
                <div key={index} className={`flex ${msg.from === 'agent' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`chat-bubble ${msg.from === 'agent' ? 'chat-human' : 'chat-user'}`}>
                    <p className="text-sm">{msg.text}</p>
                    <p className={`text-xs mt-1 ${msg.from === 'agent' ? 'text-blue-200' : 'text-gray-500'} text-right`}>
                      {formatTimestamp(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            
            {/* Área de Composición y Seguimiento */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
              <textarea 
                rows="3" 
                placeholder="Escribir mensaje de WhatsApp..." 
                className="w-full p-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                disabled={!currentTarget}
              ></textarea>
              <div className="flex justify-between items-center mt-3">
                <div>
                  <span className="text-sm font-medium text-gray-700 mr-2">Registrar como Actividad:</span>
                  <button type="button" className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200">Registrar Llamada</button>
                  <button type="button" className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200">Generar Cotización</button>
                  <button type="button" className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200">Enviar Email</button>
                </div>
                <button 
                  type="submit" 
                  className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={!currentTarget || !inputText.trim()}
                >
                  Enviar
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      {/* ------------------------------------------------------ */}
      {/* PANEL DERECHO: Ficha CRM (Data-Sync)                   */}
      {/* ------------------------------------------------------ */}
      <div className="w-full md:w-1/4 h-full flex flex-col bg-white border-l border-gray-200">
        {!selectedCrmData ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <p className="text-gray-500 text-center">
              {currentTarget ? 'No hay datos de CRM para este número.' : 'Selecciona un chat para ver la ficha del cliente.'}
            </p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Ficha de {selectedCrmData.tipo === 'prospecto' ? 'Prospecto' : 'Cliente'}</h2>
            </div>
            
            <div className="flex-1 overflow-y-auto thin-scrollbar p-4 space-y-4">
              
              {/* Sección de Etapas (si es prospecto) */}
              {selectedCrmData.tipo === 'prospecto' && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="text-xs font-medium text-gray-500">Etapa del Prospecto</label>
                  <select defaultValue={selectedCrmData.etapa} className="w-full mt-1 p-2 border border-gray-300 rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Nuevo">Nuevo</option>
                    <option value="Contactado">Contactado</option>
                    <option value="Negociación">Negociación</option>
                    <option value="Alta">Alta</option>
                    <option value="Perdido">Perdido</option>
                  </select>
                </div>
              )}

              {/* Sección "GENERALES" */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">Generales</h4>
                <p className="text-sm"><strong className="text-gray-600">Empresa:</strong> {selectedCrmData.empresa}</p>
                <p className="text-sm"><strong className="text-gray-600">RFC:</strong> {selectedCrmData.rfc}</p>
                <p className="text-sm"><strong className="text-gray-600">Contacto:</strong> {selectedCrmData.contacto}</p>
                <p className="text-sm"><strong className="text-gray-600">Puesto:</strong> {selectedCrmData.puesto}</p>
                <p className="text-sm"><strong className="text-gray-600">Teléfono:</strong> {selectedCrmData.telefono}</p>
                <p className="text-sm"><strong className="text-gray-600">Email:</strong> {selectedCrmData.email}</p>
                <p className="text-sm"><strong className="text-gray-600">Origen:</strong> {selectedCrmData.origen}</p>
              </div>
              
              {/* Sección "ADICIONALES" */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">Adicionales</h4>
                <p className="text-sm"><strong className="text-gray-600">Categoría:</strong> {selectedCrmData.adicionales.categoria}</p>
                <p className="text-sm"><strong className="text-gray-600">Fact. Mensual:</strong> {selectedCrmData.adicionales.facturacionMensual}</p>
                <p className="text-sm"><strong className="text-gray-600">Crédito:</strong> <span className={`font-medium ${selectedCrmData.adicionales.credito === 'SÍ' ? 'text-green-600' : 'text-red-600'}`}>{selectedCrmData.adicionales.credito}</span></p>
                <p className="text-sm"><strong className="text-gray-600">Plazo:</strong> {selectedCrmData.adicionales.plazoDias} días</p>
              </div>

              {/* Sección "ENTREGAS" */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">Entregas</h4>
                <p className="text-sm"><strong className="text-gray-600">Dirección:</strong> {selectedCrmData.entregas.direccion}</p>
                <p className="text-sm"><strong className="text-gray-600">Horario:</strong> {selectedCrmData.entregas.horario}</p>
                <p className="text-sm"><strong className="text-gray-600">Encargado:</strong> {selectedCrmData.entregas.encargado}</p>
              </div>

              {/* Sección "AVANCE/OBSERVACIONES" */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">Notas / Avance</h4>
                <textarea 
                  rows="5" 
                  className="w-full p-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  placeholder="Añadir nota de seguimiento..."
                  defaultValue={selectedCrmData.notas}
                ></textarea>
              </div>

            </div>
          </>
        )}
      </div>

    </div>
  );
}

export default WhatsappWorkstation;
